import React from 'react';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { AnalyseTemplateForm } from './AnalyseTemplateForm';
import { AnalyseTemplateFormValues } from '../types/AnalyseTemplateFormValues';
import { validateAnalysisAutomationTasks } from 'modules/analyse/utils/validateAutomationTasks';
import { AutomationTaskInput } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useUser } from 'core/stores/hooks/useUser';
import { FailedMessageWithHref } from 'common/ui';
import { useCreateAutomationTasksTemplateMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useDrawer } from 'services/drawer/hooks';

const container: SxProps = {
  minWidth: '700px',
  p: 3
};

type Props = {
  onTemplateCreated?: (id: string) => void;
};

export const TemplateCreationDrawer: React.FC<Props> = ({
  onTemplateCreated
}) => {
  const { showToast } = useToast();
  const { closeDrawer } = useDrawer();
  const organization = useOrganization();
  const user = useUser();

  const [createAutomationTasksTemplateMutation] =
    useCreateAutomationTasksTemplateMutation();

  const onFormCompleted = async ({
    templateName,
    ruleAutomationTasks,
    actionAutomationTasks
  }: AnalyseTemplateFormValues) => {
    const automationTasks = [...actionAutomationTasks, ...ruleAutomationTasks];

    try {
      const { isDuplicates, validAutomationTasks } =
        validateAnalysisAutomationTasks(automationTasks || []);

      if (isDuplicates) {
        showToast({
          message:
            'You have conflicting automations. Remove them before saving.',
          type: ToastType.Error
        });
        return;
      }

      const formattedAutomationTasks =
        validAutomationTasks.map<AutomationTaskInput>((item) => {
          return {
            action: item.action,
            requirementConditions: item.requirementConditions,
            isParent: item.isParent
          };
        });

      const { data } = await createAutomationTasksTemplateMutation({
        variables: {
          input: {
            title: templateName,
            automationTasks: formattedAutomationTasks,
            organizationId: organization.id,
            userId: user.id
          }
        }
      });

      closeDrawer();

      onTemplateCreated?.(data?.createAutomationTasksTemplate?.id || '');

      showToast({
        title: 'Template added',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try creating the template again."
            emailSubject="Failed to create template"
          />
        ),
        type: ToastType.Error
      });
    }
  };

  return (
    <Box sx={container}>
      <Stack direction="column" gap="40px">
        <Typography fontSize="30px" fontWeight="400" lineHeight="38px">
          Create new template
        </Typography>
        <AnalyseTemplateForm
          isVerticalForm
          onFormSubmitted={onFormCompleted}
          onCanceled={closeDrawer}
          submitButtonLabel="Save template"
        />
      </Stack>
    </Box>
  );
};
